/**
 * Patient Requirements — shared constants (GDN-3224, PRD Slice A).
 */

/**
 * Requirement kinds the backend accepts. Mirrors the `kind` pattern on
 * DefinitionCreateRequest (`^(payment_method|insurance_card|form)$`); the
 * generated type is a bare `string`, so this is the FE's narrowing.
 */
export const REQUIREMENT_KINDS = ['payment_method', 'insurance_card', 'form'] as const;

export type RequirementKind = (typeof REQUIREMENT_KINDS)[number];

/**
 * Kinds an org may hold at most one of. AC-1: "Staff cannot create a second
 * payment-method requirement for the same org." The backend enforces this with
 * a 409 (GDN-3222); the catalog withholds the option so staff never reach it.
 */
export const SINGLETON_KINDS: readonly RequirementKind[] = ['payment_method'];

/**
 * Kinds the catalog can create today. `form` requirements are Slice C
 * (AC-17 … AC-19, GDN-3229/GDN-3231) and are created from a form, not here —
 * offering the bare kind would produce a definition with nothing to submit.
 */
export const CREATABLE_KINDS: readonly RequirementKind[] = [
  'payment_method',
  'insurance_card',
];

/** Staff-facing names for each kind. Not the patient-facing label. */
export const REQUIREMENT_KIND_LABELS: Record<RequirementKind, string> = {
  payment_method: 'Payment method on file',
  insurance_card: 'Insurance card',
  form: 'Form',
};

export const isRequirementKind = (value: string): value is RequirementKind =>
  (REQUIREMENT_KINDS as readonly string[]).includes(value);

/**
 * Staff-facing name for a definition's kind, falling back to the raw value so a
 * kind added by the backend ahead of the frontend still renders something real
 * rather than an empty cell.
 */
export const requirementKindLabel = (kind: string): string =>
  isRequirementKind(kind) ? REQUIREMENT_KIND_LABELS[kind] : kind;

/**
 * What a patient is told this requirement is, per type — a SEPARATE map from
 * the staff labels above, on purpose.
 *
 * Prefilling from the staff map would ship "on file" to patients: that is the
 * practice's bookkeeping view of a card, not the patient's view of the thing
 * they are being asked to provide. `insurance_card` happens to read the same
 * in both; it is repeated here rather than shared so the two maps stay
 * independently editable.
 *
 * `form` has no useful type-level default — the real one is the selected
 * form's own title, which the caller supplies. The static string below is a
 * fallback for a form with no title, and should almost never render.
 */
export const PATIENT_FACING_KIND_DEFAULTS: Record<RequirementKind, string> = {
  payment_method: 'Payment method',
  insurance_card: 'Insurance card',
  form: 'Form to sign',
};

/**
 * The name to prefill for a chosen type, given the selected form's title when
 * the type is `form`. A blank or whitespace-only title falls back rather than
 * prefilling an empty name.
 */
export const patientFacingDefault = (
  kind: string,
  formTitle?: string | null,
): string => {
  if (kind === 'form') return formTitle?.trim() || PATIENT_FACING_KIND_DEFAULTS.form;
  return isRequirementKind(kind) ? PATIENT_FACING_KIND_DEFAULTS[kind] : '';
};

/** Patient-facing label bound, matched to the settings text fields around it. */
export const MAX_LABEL_LENGTH = 120;
